import { ModuleWithProviders, NgModule } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DragDropModule } from '@angular/cdk/drag-drop';

// PS Modules
import { TabsModule } from '@purespectrum1/ui/tabs';
import { ModalModule } from '@purespectrum1/ui/modal';
import { AccordionModule } from '@purespectrum1/ui/accordion';
import { DropdownModule } from '@purespectrum1/ui/dropdown';
import { LoaderModule } from '@purespectrum1/ui/loader';
import { ButtonModule } from '@purespectrum1/ui/button';
import { GoogleTagManagerModule } from '@purespectrum1/ui/tracking';
import { SliderModule } from '@purespectrum1/ui/slider';
import { SelectDropdownModule } from '@purespectrum1/ui/select-dropdown';

// NG Modules
import { NgSelectModule } from '@ng-select/ng-select';
import { Daterangepicker } from 'ng2-daterangepicker';

// Modules
import { HasRoleModule } from '../shared/ui/has-role/has-role.module';
import { SortTableModule } from '../shared/ui/sort-table/sort-table.module';
import { SurveysTableModule } from '../shared/ui/surveys-table/surveys-table.module';
import { SearchFilterPipeModule } from '../create-surveys/shared/pipes/filter-pipe.module';
import { PdsLabelModule } from '../shared/ui/pds-label/pds-label.module';
import { ConfigEnabledModule } from '../create-surveys/shared/ui/config-enabled/config-enabled.module';

// Pipes
import { CapingPipe } from '../shared/pipes/cap-pipe';
import { CurrencySymbolPipe } from '../shared/pipes/currency-units-pipe';

// Interfaces
import { EnvironmentConfig } from '../shared/interfaces/environment.interface';

// Components
import { ManageSurveyTabsComponent } from './manage-survey-tabs.component';
import { ManageQuotasComponent } from './manage-quotas/manage-quotas.component';
import { ManageLinksComponent } from './manage-links/manage-links.component';
import { ManageReconcileComponent } from './manage-reconcile/manage-reconcile.component';
import { AdjustmentFileUploadComponent } from './manage-reconcile/adjustment-file-upload/adjustment-file-upload.component';
import { AdjustmentModalComponent } from './manage-reconcile/adjustment-modal/adjustment-modal.component';
import { ManageReportComponent } from './manage-report/manage-report.component';
import { MarginReportComponent } from './margin-report/margin-report.component';
import { ManageAudienceComponent } from './manage-audience/manage-audience.component';
import { ManageSuppliersComponent } from './manage-suppliers/manage-suppliers.component';
import { ManageActivityLogsComponent } from './manage-activity-logs/manage-activity-logs.component';
import { ManageMultiCountryComponent } from './manage-multi-country/manage-multi-country.component';
import { PriceViewBlockComponent } from './manage-quotas/price-view-block/price-view-block.component';
import { QuotasInfoModalComponent } from './manage-quotas/quotas-info-modal/quotas-info-modal.component';
import { AdjustmentTableComponent } from './manage-reconcile/adjustment-table/adjustment-table.component';
import { RenameQuotaModalComponent } from './manage-quotas/rename-quota-modal/rename-quota-modal.component';
import { ManageTrafficChannelsComponent } from './manage-traffic-channels/manage-traffic-channels.component';
import { EditCurrentValueComponent } from './manage-quotas/edit-value-target/edit-current-value.component';
import { SubscribeEmailModalComponent } from './manage-report/subscribe-email-modal/subscribe-email-modal.component';
import { ClearUploadModalComponent } from './manage-traffic-channels/clear-upload-modal/clear-upload-modal.component';
import { CustomizeReportModalComponent } from './manage-report/customize-report-modal/customize-report-modal.component';
import { ViewRepondentModalComponent } from './manage-traffic-channels/view-repondent-modal/view-repondent-modal.component';
import { TrafficChannelModalComponent } from './manage-traffic-channels/traffic-channel-modal/traffic-channel-modal.component';
import { ManageHealthChecksComponent } from './manage-health-checks/manage-health-checks.component';
import { BillingNumberTooltipModule } from '../shared/ui/billing-number-tooltip/billing-number-tooltip.module';

export * from './manage-survey-tabs.component';
export * from './manage-traffic-channels/manage-traffic-channels.component';
export * from './manage-traffic-channels/traffic-channel-modal/traffic-channel-modal.component';
export * from './manage-report/customize-report-modal/customize-report-modal.component';

@NgModule({
  declarations: [
    ManageSurveyTabsComponent,
    ManageQuotasComponent,
    ManageLinksComponent,
    ManageReconcileComponent,
    AdjustmentFileUploadComponent,
    AdjustmentModalComponent,
    AdjustmentTableComponent,
    ManageAudienceComponent,
    CurrencySymbolPipe,
    CapingPipe,
    ManageSuppliersComponent,
    ManageReportComponent,
    MarginReportComponent,
    ManageActivityLogsComponent,
    ManageTrafficChannelsComponent,
    TrafficChannelModalComponent,
    ViewRepondentModalComponent,
    ClearUploadModalComponent,
    ManageMultiCountryComponent,
    EditCurrentValueComponent,
    QuotasInfoModalComponent,
    RenameQuotaModalComponent,
    PriceViewBlockComponent,
    CustomizeReportModalComponent,
    SubscribeEmailModalComponent,
    ManageHealthChecksComponent,
  ],
  exports: [
    ManageSurveyTabsComponent,
    ManageTrafficChannelsComponent,
    ManageMultiCountryComponent,
  ],
  imports: [
    CommonModule,
    TabsModule,
    FormsModule,
    HasRoleModule,
    ModalModule,
    SortTableModule,
    AccordionModule,
    DropdownModule,
    ButtonModule,
    LoaderModule,
    SurveysTableModule,
    PdsLabelModule,
    NgSelectModule,
    Daterangepicker,
    GoogleTagManagerModule,
    SearchFilterPipeModule,
    DragDropModule,
    ConfigEnabledModule,
    SelectDropdownModule,
    ReactiveFormsModule,
    SliderModule,
    BillingNumberTooltipModule,
  ],
  providers: [DatePipe],
})
export class ManageSurveyTabsModule {
  static forRoot(
    config: EnvironmentConfig
  ): ModuleWithProviders<ManageSurveyTabsModule> {
    return {
      ngModule: ManageSurveyTabsModule,
      providers: [
        {
          provide: 'ENV',
          useValue: config || {},
        },
      ],
    };
  }
}
